import * as React from 'react'

import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import usersWithRegularAccess from 'modules/core/lib/usersWithRegularAccess'
import users, { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employees from 'modules/employees/store/collections/employees'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'

interface Props {
  members: number[]
  setMembers: (ids: number[]) => void
  everybody?: boolean
  allMyDescendants: number[]
}

export function computeAvailableUsers(
  userId: number,
  everybody = false,
  allMyDescendants: number[],
  canCreateEverybody: boolean,
  canCreateReportees: boolean,
  canCreateTeam: boolean,
  canCreateOwned: boolean
): User[] {
  if (everybody || canCreateEverybody) {
    return usersWithRegularAccess(users.active)
  }

  let list = [users.current]

  const currentEmployee = users.current.employee

  if (!currentEmployee) return list

  if (canCreateReportees) {
    const reportingToMeDirectly = employees.filter(
      (employee) => employee.attributes.manager_id === currentEmployee.id
    )

    list = list.concat(reportingToMeDirectly.map((e) => employeeUser(e)))
  }

  if (canCreateTeam) {
    const reportingToMe = employees.filter((employee) => allMyDescendants.includes(employee.id))

    list = list.concat(reportingToMe.map((e) => employeeUser(e)))
  }

  if (!canCreateOwned) {
    list = list.filter((user) => user.id !== userId)
  }

  list = list.filter(
    (user) => hasRegularAccess(!!user.employee?.attributes.has_regular_access) || !user.employee
  )

  return [...new Set(list)]
}

function ManualSelection({ members, setMembers, everybody = false }: Props) {
  const { canCreateEverybody, canCreateReportees, canCreateTeam, canCreateOwned } =
    useEngagementContext()
  const { allMyDescendants } = useCurrentSession()
  const _setMembers = (ids: number[]) => setMembers(ids)

  const {
    access: { id: userId },
  } = useCurrentSession()
  const setMember = (id: number | null | undefined) => {
    if (!id) return setMembers([])

    setMembers(xor(members, [id]))
  }

  // This is a bit of an expensive computation, so we are memoizing it at the
  // expense of less reactivity.
  const availableUsers = React.useRef(
    computeAvailableUsers(
      userId,
      everybody,
      allMyDescendants,
      canCreateEverybody,
      canCreateReportees,
      canCreateTeam,
      canCreateOwned
    )
  ).current

  return (
    <UserSelector
      users={availableUsers}
      onSelect={setMember}
      onSelectMultiple={_setMembers}
      selectedIds={members}
      hideFields={[Fields.EmployeeStatus, Fields.UserType]}
    />
  )
}

export default observer(ManualSelection)
